import {
  CdvPurchase, Platform, ProductType, store,
} from 'capacitor-plugin-cdv-purchase';
import { playBillingApi, PlayBillingSubscription } from './api';

const PRODUCT_ID = 'standlogic_plus';
const PLAN_IDS = ['monthly', 'annual'] as const;
type Plan = typeof PLAN_IDS[number];
// "annual" is the auto-renewing base plan; the original prepaid "yearly" plan is retired.
const BASE_PLAN_IDS: Record<Plan, string> = { monthly: 'monthly', annual: 'annual' };

interface Handlers {
  verified: (subscription: PlayBillingSubscription) => void;
  failed: (message: string) => void;
}

let initialization: Promise<void> | undefined;
let handlers: Handlers | undefined;
const processedTransactions = new Set<string>();

function reportFailure(error: unknown): string {
  return error instanceof Error ? error.message : 'Google Play purchase verification failed.';
}

async function verifyTransaction(transaction: CdvPurchase.Transaction, throwOnError = false): Promise<void> {
  if (transaction.platform !== Platform.GOOGLE_PLAY ||
      !transaction.products.some((product) => product.id === PRODUCT_ID) ||
      !transaction.purchaseId || processedTransactions.has(transaction.transactionId)) return;
  if (transaction.isPending) return;
  processedTransactions.add(transaction.transactionId);
  try {
    const { data } = await playBillingApi.verify(transaction.purchaseId);
    await transaction.finish();
    handlers?.verified(data.subscription);
  } catch (error) {
    processedTransactions.delete(transaction.transactionId);
    if (throwOnError) throw new Error(reportFailure(error));
    handlers?.failed(reportFailure(error));
  }
}

export async function initializePlayBilling(userId: string, nextHandlers: Handlers): Promise<void> {
  handlers = nextHandlers;
  store.applicationUsername = () => userId;
  store.obfuscator = 'legacy';
  if (!initialization) {
    store.register({
      id: PRODUCT_ID,
      type: ProductType.PAID_SUBSCRIPTION,
      platform: Platform.GOOGLE_PLAY,
    });
    store.when()
      .approved((transaction) => { void verifyTransaction(transaction); })
      .pending(() => handlers?.failed('Your purchase is pending Google Play payment approval.'));
    store.error((error) => handlers?.failed(error.message));
    initialization = store.initialize([Platform.GOOGLE_PLAY]).then((errors) => {
      const failure = errors.find((error) => error.isError);
      if (failure) throw new Error(failure.message);
    }).then(() => new Promise<void>((resolve, reject) => {
      if (store.isReady) {
        resolve();
        return;
      }
      const timeout = window.setTimeout(() => reject(new Error('Google Play is taking too long to load subscription plans.')), 20_000);
      store.ready(() => {
        window.clearTimeout(timeout);
        resolve();
      });
    }));
  }
  await initialization;
}

export function playPlanPrice(plan: Plan): string | undefined {
  const offerId = `${PRODUCT_ID}@${BASE_PLAN_IDS[plan]}`;
  return store.get(PRODUCT_ID, Platform.GOOGLE_PLAY)?.offers
    .find((offer) => offer.id === offerId)?.pricingPhases[0]?.price;
}

export async function purchasePlayPlan(plan: Plan): Promise<void> {
  if (!PLAN_IDS.includes(plan)) throw new Error('Choose a valid Google Play subscription plan.');
  const offer = store.get(PRODUCT_ID, Platform.GOOGLE_PLAY)?.getOffer(`${PRODUCT_ID}@${BASE_PLAN_IDS[plan]}`);
  if (!offer?.canPurchase) throw new Error('This Google Play subscription plan is not currently available.');
  const error = await offer.order();
  if (error) throw new Error(error.message);
}

export async function restorePlayPurchases(): Promise<void> {
  const error = await store.restorePurchases();
  if (error) throw new Error(error.message);
  await store.update();
  await Promise.all(store.localTransactions
    .filter((transaction) =>
      transaction.platform === Platform.GOOGLE_PLAY &&
      transaction.products.some((product) => product.id === PRODUCT_ID) &&
      Boolean(transaction.purchaseId)
    )
    .map((transaction) => verifyTransaction(transaction, true)));
}

export async function managePlaySubscriptions(): Promise<void> {
  const error = await store.manageSubscriptions(Platform.GOOGLE_PLAY);
  if (error) throw new Error(error.message);
}

export function isPlayStoreReady(): boolean {
  return store.isReady && Boolean(store.get(PRODUCT_ID, Platform.GOOGLE_PLAY));
}
